import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { ConvergePayButton } from '../../components/ConvergePayButton';
import { Tag, X } from 'lucide-react';

/**
 * Right-side sticky cost summary card on the Review Order page.
 *
 * Shows item count, subtotal, optional discount, shipping, tax placeholder,
 * the grand total, plus a Special Instructions textarea and the
 * "REQUEST ORDER" submit button. Purely presentational — all state lives
 * in the parent.
 *
 * Discount UI (iter105): an "Apply discount code" inline form that
 * surfaces a "Discount (CODE)" line above shipping when a code is
 * applied, plus a small × button to remove it. Validation + persistence
 * happens server-side via /api/discounts/{validate,apply,remove}.
 */
export const CostSummaryCard = ({
  itemCount,
  subtotal,
  shipping,
  tax = 0,
  taxLabel = 'Tax',
  total,
  formatCurrency,
  currencyCode = 'USD',
  specialInstructions,
  onChangeSpecialInstructions,
  submitting,
  onSubmit,
  // Payment props — when orderId + total are present, we surface the
  // "Pay with Card" Converge CTA above "Request Order". The order
  // authorization (not capture) happens here; capture fires when the
  // admin approves the proof.
  // When `billingMode === 'invoice'`, the Converge block is suppressed
  // entirely and a Net-30 notice replaces it — the customer skips
  // self-checkout and we email them the PDF order confirmation post-submit.
  orderId,
  paymentStatus,
  onPaymentAuthorized,
  billingMode = 'pay_now',
  // Admin bypass — staff users skip both Converge AND Net-30 notice;
  // they see a small "Admin order" pill explaining the bypass and a
  // submit button labelled accordingly.
  isAdmin = false,
  // Discount props (iter105)
  discountCode = null,
  discountAmount = 0,
  discountPercent = 0,
  discountBusy = false,
  discountError = null,
  onApplyDiscount = () => {},
  onRemoveDiscount = () => {},
}) => {
  const [codeInput, setCodeInput] = useState('');

  const handleApplyClick = () => {
    if (!codeInput.trim() || discountBusy) return;
    onApplyDiscount(codeInput);
    setCodeInput('');
  };
  const handleApplyKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyClick();
    }
  };

  const paymentLocked = paymentStatus === 'authorized' || paymentStatus === 'captured';

  // iter114s.40 — `total` is now always populated (defaults to
  // subtotal + tax when no shipping rate is picked). To keep the
  // pay-now Converge auth flow safe — i.e. block card-authorization
  // until the customer's shipping is known so we don't authorize a
  // wrong amount — we derive a separate "shipping locked in" flag
  // from the raw `shipping` prop and gate the Converge button on it.
  // Net-30 mode bypasses this entirely (shipping is settled on the
  // emailed invoice, no card-auth in this flow).
  const shippingKnown = shipping !== null && shipping !== undefined;

  return (
    <div
      className="bg-gray-100 rounded-lg shadow-lg p-6"
      data-testid="review-cost-summary"
    >
      <div className="space-y-3 mb-4">
        <div className="flex justify-between text-gray-700">
          <span>Total Items</span>
          <span className="font-semibold">{itemCount}</span>
        </div>
        <div className="flex justify-between text-gray-700">
          <span>Total Cost</span>
          <span className="font-semibold">{formatCurrency(subtotal)}</span>
        </div>
        {discountCode && discountAmount > 0 && (
          <div
            className="flex justify-between items-center text-emerald-700"
            data-testid="review-discount-line"
          >
            <span className="flex items-center gap-1.5">
              <Tag className="w-3.5 h-3.5" />
              <span>
                Discount{' '}
                <span className="font-mono text-xs bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded ml-0.5" data-testid="review-discount-code-pill">
                  {discountCode}
                </span>{' '}
                <span className="text-xs text-emerald-600">({discountPercent}% off)</span>
              </span>
              {!paymentLocked && (
                <button
                  type="button"
                  onClick={onRemoveDiscount}
                  disabled={discountBusy}
                  className="ml-1 inline-flex items-center justify-center rounded-full p-0.5 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-800 disabled:opacity-50"
                  aria-label="Remove discount"
                  data-testid="review-discount-remove-btn"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
            <span className="font-semibold" data-testid="review-discount-amount">
              −{formatCurrency(discountAmount)}
            </span>
          </div>
        )}
        <div className="flex justify-between text-gray-700">
          <span>Shipping &amp; Handling*</span>
          <span className="font-semibold" data-testid="review-shipping-amount">
            {/* iter114s.40 — TBD fallback retired. When no rate is
                picked we show $0.00 and rely on the asterisk footnote
                ("* estimated shipping costs are subject to change…")
                to set expectations, instead of hiding the line item
                behind a vague "TBD" label. */}
            {formatCurrency(shipping || 0)}
          </span>
        </div>
        <div className="flex justify-between text-gray-700" data-testid="review-tax-line">
          <span>{taxLabel}</span>
          <span className="font-semibold">
            {formatCurrency(tax || 0)}
          </span>
        </div>
        <div className="border-t-2 border-gray-300 pt-3">
          <div className="flex justify-between text-xl font-bold text-gray-900">
            <span>Total ({currencyCode})</span>
            <span data-testid="review-grand-total">
              {formatCurrency(total || 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Discount-code input — hidden once a code is applied (the
          discount line above takes over with a remove × button) and
          when payment is already locked in (refund deltas are admin
          territory). */}
      {!discountCode && !paymentLocked && (
        <div className="mb-4" data-testid="review-discount-input-block">
          <label className="block text-xs font-medium text-gray-600 mb-1.5">
            Have a discount code?
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              onKeyDown={handleApplyKeyDown}
              placeholder=""
              maxLength={32}
              disabled={discountBusy}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent text-sm font-mono uppercase tracking-wide disabled:bg-gray-50"
              data-testid="review-discount-code-input"
              autoComplete="off"
              spellCheck="false"
            />
            <Button
              type="button"
              onClick={handleApplyClick}
              disabled={!codeInput.trim() || discountBusy}
              variant="outline"
              className="px-4 py-2 border-[#4A7C59] text-[#4A7C59] hover:bg-[#4A7C59] hover:text-white text-sm"
              data-testid="review-discount-apply-btn"
            >
              {discountBusy ? '…' : 'Apply'}
            </Button>
          </div>
          {discountError && (
            <p
              className="mt-1.5 text-xs text-red-600"
              data-testid="review-discount-error"
            >
              {discountError}
            </p>
          )}
        </div>
      )}

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Special instructions
        </label>
        <textarea
          value={specialInstructions}
          onChange={onChangeSpecialInstructions}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent text-sm"
          placeholder="Any special instructions for your order..."
          data-testid="review-special-instructions"
        />
      </div>

      {orderId && shippingKnown && total > 0 && !isAdmin && billingMode !== 'invoice' && (
        <div className="mb-3" data-testid="review-payment-block">
          <ConvergePayButton
            orderId={orderId}
            amount={total}
            disabled={submitting || paymentLocked}
            onAuthorized={onPaymentAuthorized}
            className="w-full py-3"
            label={`Pay ${formatCurrency(total)} with Card`}
          />
          <p className="text-[11px] text-gray-500 mt-1 text-center">
            Your card is authorized now — we only capture funds once your proof is approved.
          </p>
        </div>
      )}

      {orderId && !shippingKnown && !isAdmin && billingMode !== 'invoice' && (
        <div
          className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
          data-testid="review-shipping-required-notice"
        >
          <p className="text-xs text-amber-800 leading-relaxed">
            <span className="font-semibold">Shipping rate required.</span> Pick a
            shipping option above to see your final total and pay.
          </p>
        </div>
      )}

      {!isAdmin && billingMode === 'invoice' && total > 0 && (
        <div
          className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
          data-testid="review-net30-notice"
        >
          <p className="text-sm font-semibold text-amber-900 mb-1">
            Net 30 invoicing
          </p>
          <p className="text-xs text-amber-800 leading-relaxed">
            No card needed at checkout — we&apos;ll email you a PDF order
            confirmation right after your order is placed. Payment terms:
            Net 30 from the order date.
          </p>
        </div>
      )}

      {isAdmin && (
        <div
          className="mb-3 rounded-lg border border-purple-300 bg-purple-50 px-4 py-3"
          data-testid="review-admin-bypass-notice"
        >
          <p className="text-sm font-semibold text-purple-900 mb-1">
            Admin order bypass
          </p>
          <p className="text-xs text-purple-800 leading-relaxed">
            No payment or invoice required — this order will be submitted
            directly to production with <code className="font-mono text-[11px]">submitted_by_admin=true</code>{' '}
            stamped server-side. Use sparingly for sample runs and customer-service back-fills.
          </p>
        </div>
      )}

      <Button
        onClick={onSubmit}
        disabled={submitting || (!isAdmin && billingMode !== 'invoice' && !shippingKnown)}
        data-testid="request-order-btn"
        className="w-full bg-[#6FBF73] hover:bg-[#4A7C59] text-white py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
      >
        {submitting
          ? 'Submitting...'
          : isAdmin
            ? 'SUBMIT ORDER (ADMIN)'
            : (billingMode === 'invoice' ? 'PLACE ORDER ON NET-30 INVOICE' : 'REQUEST ORDER')}
      </Button>

      <p className="text-xs text-gray-500 mt-3 italic">
        * estimated shipping costs are subject to change upon order confirmation
      </p>
    </div>
  );
};
